import {
  EventFeedFilterGroupKeys,
  EventFeedFiltersSchemas,
  eventFeed,
} from '@api';
import { SharedFilterState } from '@terminal/hooks/use-filter-actions/useFilterActions';
import {
  INSIGHT_FEED_INCLUDE_COMPETITORS,
  INSIGHT_FEED_SELECTED_WATCHLISTS,
  useRemoteStorage,
  WHATS_NEW_INCLUDE_COMPETITORS,
  WHATS_NEW_IS_FIRST_TIME_EVER,
  WHATS_NEW_SELECTED_WATCHLISTS,
} from '@terminal/hooks/use-remote-storage';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { apiFetch } from '@terminal/utils/api-fetch/apiFetch';
import { mapEventFeedFiltersSchemasToFilterGroups } from '@terminal/utils/event-feed-filters/mapEventFeedSchemasToScreenerFilters';
import { create } from '@terminal/utils/store/createStore';
import {
  InsightsPill,
  MarketCatalystSubTab,
} from '@terminal/views/insight-feed/components/following/following-utils';
import { InsightFeedFilterTabs } from '@terminal/views/insight-feed/components/insight-feed-type-tab-line/InsightFeedTypeTabLine';

import {
  defaultEventFeedFilterGroups,
  tabToEventFeedFilterGroup,
  tabTypeToEventFeedType,
} from './insight-feed-filters-shared';

export { tabToEventFeedFilterGroup, tabTypeToEventFeedType };

interface InsightFeedFiltersStore
  extends SharedFilterState<EventFeedFilterGroupKeys> {
  selectedWatchlistIds: Set<string>;
  isSecondRelationshipEnabled: boolean;
  selectedWatchlistSubTab: InsightFeedFilterTabs;
  selectedPill: InsightsPill;
  selectedMarketCatalystSubTab: MarketCatalystSubTab;
  isInitialized: boolean;
  isFirstTimeEver?: boolean;
  initializeFromStorage: () => void;
  initEventFeedFilters: () => Promise<void>;
  setSelectedWatchlistIds: (ids: Set<string>) => void;
  setIsSecondRelationshipEnabled: (enabled: boolean) => void;
  handleToggleSecondRelationship: () => void;
  setSelectedWatchlistSubTab: (type: InsightFeedFilterTabs) => void;
  setSelectedPill: (pill: InsightsPill) => void;
  setSelectedMarketCatalystSubTab: (tab: MarketCatalystSubTab) => void;
  setIsFirstTimeEver: (isFirstTimeEver: boolean) => void;
}

//TODO: https://toggleai.atlassian.net/browse/PLATFORM-4096 Rename hook after followed_themes flag is removed
// to reflect that this hook is used for both insight feed and whats new widget.

export const useInsightFeedFilters = create<InsightFeedFiltersStore>(set => ({
  selectedWatchlistIds: new Set<string>(),
  isSecondRelationshipEnabled: false,
  selectedWatchlistSubTab: 'all',
  selectedPill: 'all',
  selectedMarketCatalystSubTab: 'all',
  isInitialized: false,
  filterGroups: defaultEventFeedFilterGroups,
  initializeFromStorage: () => {
    const { items } = useRemoteStorage.getState();

    const followedThemes = useFlags
      .getState()
      .getFeatureFlagState('followed_themes');

    const storedIdList = followedThemes
      ? items?.[WHATS_NEW_SELECTED_WATCHLISTS]?.map(row => row.id)
      : items?.[INSIGHT_FEED_SELECTED_WATCHLISTS];

    const competitorsStoreKey = followedThemes
      ? WHATS_NEW_INCLUDE_COMPETITORS
      : INSIGHT_FEED_INCLUDE_COMPETITORS;

    const isIncludeCompetitors =
      typeof items?.[competitorsStoreKey] === 'boolean'
        ? items[competitorsStoreKey]
        : undefined;

    const isFirstTimeEver =
      items?.[WHATS_NEW_IS_FIRST_TIME_EVER as keyof typeof items] === undefined;

    set({
      ...(storedIdList?.length && {
        selectedWatchlistIds: new Set(storedIdList),
      }),
      ...(isIncludeCompetitors !== undefined && {
        isSecondRelationshipEnabled: isIncludeCompetitors,
      }),
      isFirstTimeEver,
      isInitialized: true,
    });
  },
  initEventFeedFilters: async () => {
    const res = await apiFetch<EventFeedFiltersSchemas>(
      eventFeed.getFilters.path,
      { method: 'get' }
    );

    if (!res.error) {
      set({
        filterGroups: mapEventFeedFiltersSchemasToFilterGroups(
          res.data,
          defaultEventFeedFilterGroups
        ),
      });
    }
  },
  setSelectedWatchlistIds: ids => {
    set({ selectedWatchlistIds: ids });

    const followedThemes = useFlags
      .getState()
      .getFeatureFlagState('followed_themes');
    const { storeItems } = useRemoteStorage.getState();

    if (followedThemes) {
      storeItems({
        [WHATS_NEW_SELECTED_WATCHLISTS]: Array.from(ids).map(id => ({ id })),
      });
    } else {
      storeItems({
        [INSIGHT_FEED_SELECTED_WATCHLISTS]: Array.from(ids),
      });
    }
  },
  setIsSecondRelationshipEnabled: enabled => {
    set({ isSecondRelationshipEnabled: enabled });

    const followedThemes = useFlags
      .getState()
      .getFeatureFlagState('followed_themes');
    const { storeItems } = useRemoteStorage.getState();

    if (followedThemes) {
      storeItems({ [WHATS_NEW_INCLUDE_COMPETITORS]: enabled });
    } else {
      storeItems({ [INSIGHT_FEED_INCLUDE_COMPETITORS]: enabled });
    }
  },
  handleToggleSecondRelationship: () => {
    const next = !useInsightFeedFilters.getState().isSecondRelationshipEnabled;
    set({ isSecondRelationshipEnabled: next });

    const followedThemes = useFlags
      .getState()
      .getFeatureFlagState('followed_themes');
    const { storeItems } = useRemoteStorage.getState();

    if (followedThemes) {
      storeItems({ [WHATS_NEW_INCLUDE_COMPETITORS]: next });
    } else {
      storeItems({ [INSIGHT_FEED_INCLUDE_COMPETITORS]: next });
    }
  },
  setSelectedWatchlistSubTab: type => {
    set({ selectedWatchlistSubTab: type });
  },
  setSelectedPill: pill => {
    set({ selectedPill: pill });
  },
  setSelectedMarketCatalystSubTab: tab => {
    set({ selectedMarketCatalystSubTab: tab });
  },
  setIsFirstTimeEver: isFirstTimeEver => {
    const { storeItems } = useRemoteStorage.getState();
    set({ isFirstTimeEver });
    storeItems({ [WHATS_NEW_IS_FIRST_TIME_EVER]: isFirstTimeEver });
  },
}));
